// src/pages/admin/orders/OrderList.js
import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, startAfter, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../../styles/tokens';
import { useIsMobile } from '../../../hooks/useIsMobile';

const STATUS_OPTIONS = ['open','active','completed','disputed','cancelled'];
const NEUTRAL_CHIP = { color: COLORS.textLight, background: COLORS.surfaceHover };
const STATUS_CHIP = {
  open:      { color: COLORS.warningText, background: COLORS.warningBg },
  active:    { color: COLORS.successText, background: COLORS.successBg },
  completed: { color: COLORS.blueText,    background: COLORS.blueBg },
  disputed:  { color: COLORS.dangerText,  background: COLORS.dangerBg },
  cancelled: NEUTRAL_CHIP,
};

export default function OrderList() {
  const [orders,     setOrders]   = useState([]);
  const [lastDoc,    setLastDoc]  = useState(null);
  const [hasMore,    setHasMore]  = useState(true);
  const [loading,    setLoading]  = useState(false);
  const [loadError,  setLoadError] = useState('');
  const [statusFilter, setStatus] = useState('');
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  async function load(reset = false) {
    setLoading(true);
    setLoadError('');
    try {
      let q;
      if (statusFilter) {
        q = query(collection(db,'orders'), where('status','==',statusFilter), orderBy('createdAt','desc'), limit(50));
      } else {
        q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(50));
      }
      if (!reset && lastDoc) q = query(q, startAfter(lastDoc));
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setOrders(prev => reset ? docs : [...prev, ...docs]);
      setLastDoc(snap.docs[snap.docs.length - 1] ?? null);
      setHasMore(snap.docs.length === 50);
    } catch (e) {
      setLoadError('Kunne ikke laste ordrer. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(true); }, [statusFilter]);

  return (
    <div style={{ padding: isMobile ? 16 : 24, flex: 1, overflowY: 'auto' }}>
      <div style={{ display:'flex', gap:12, marginBottom:16, alignItems:'center' }}>
        <h2 style={{ margin:0, fontSize:18, fontWeight:700 }}>Ordrer</h2>
        <select value={statusFilter} onChange={e => setStatus(e.target.value)} style={{ padding:'6px 10px', borderRadius:6, border:`1px solid ${COLORS.border}`, fontSize:13, background:COLORS.white, color:COLORS.textDark }}>
          <option value="">Alle statuser</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loadError && <p style={{ color:COLORS.dangerText, fontSize:13, marginBottom:12 }}>{loadError}</p>}

      <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', minWidth:640, borderCollapse:'collapse', fontSize:13 }}>
        <thead>
          <tr style={{ borderBottom:`2px solid ${COLORS.border}`, textAlign:'left' }}>
            {['Ordre-ID','Bestiller','Sjåfør','Dato','Beløp','Status'].map(h =>
              <th key={h} style={{ padding:'8px 12px', color:COLORS.textLight, fontWeight:600, textTransform:'uppercase', fontSize:11 }}>{h}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {orders.map(o => (
            <tr key={o.id} onClick={() => navigate(`/admin/orders/${o.id}`)} style={{ borderBottom:`1px solid ${COLORS.border}`, cursor:'pointer' }}>
              <td style={{ padding:'10px 12px', fontFamily:'monospace', fontSize:12 }}>{o.id.slice(0,8)}…</td>
              <td style={{ padding:'10px 12px' }}>{o.source === 'carrier' ? `${o.customer?.name ?? 'Egen kunde'} (egen ordre)` : (o.ordererName ?? o.ordererId ?? '—')}</td>
              <td style={{ padding:'10px 12px' }}>{o.driverName  ?? o.driverId  ?? '—'}</td>
              <td style={{ padding:'10px 12px' }}>{o.createdAt?.toDate?.().toLocaleDateString('nb-NO') ?? '—'}</td>
              <td style={{ padding:'10px 12px' }}>{o.totalAmount ? `${o.totalAmount / 100} kr` : '—'}</td>
              <td style={{ padding:'10px 12px' }}>
                <span style={{ ...(STATUS_CHIP[o.status] ?? NEUTRAL_CHIP), padding:'2px 8px', borderRadius:4, fontWeight:600, fontSize:11 }}>
                  {o.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {loading && <p style={{ color:COLORS.textLight, padding:'12px 0' }}>Laster…</p>}
      {hasMore && !loading && (
        <button onClick={() => load(false)} style={{ marginTop:12, padding:'8px 16px', border:`1px solid ${COLORS.border}`, borderRadius:6, background:'transparent', color:COLORS.textDark, cursor:'pointer', fontSize:13 }}>
          Last inn flere
        </button>
      )}
    </div>
  );
}
